/*
** The main ErrorBag class is used to store errors and provide methods to add, remove, and clear errors.
** The Validator class is used to validate fields based on rules.
** The plugin is installed in the main.js file and provides the $validator and $setErrorBag properties to the Vue instance.
** The $validator property is used to access the errors object, and the $setErrorBag property is used to set the error bag for the component
** The code should work with both Vue 2 and Vue 3.
*/

export class ErrorBag {
    constructor() {
        this.items = [];
    }

    add(error) {
        this.items.push(error);
    }

    remove(field) {
        const index = this.items.findIndex(e => e.field === field);
        if (index !== -1) {
            this.items.splice(index, 1);
        }
    }

    clear() {
        this.items.splice(0, this.items.length);
    }

    has(field) {
        return this.items.some(e => e.field === field);
    }

    first(field) {
        const error = this.items.find(e => e.field === field);
        return error ? error.msg : null;
    }

    any() {
        return this.items.length > 0;
    }

    collect() {
        return this.items.reduce((acc, cur) => {
            acc[cur.field] = cur.msg;
            return acc;
        }, {});
    }
}

/*
** The Validator class is used to validate fields based on rules.
** The validate method is used to validate a field based on a value.
*/
export class Validator {
    constructor(rules) {
        if (!rules) {
            // Default rules
            this.rules = {
                email: 'email',
                required: 'required',
                dateOfBirth: 'dateOfBirth'
            };
        } else {
            this.rules = rules;
        }
    }

    validate(field, value) {
        if (this.rules[field]) return this[this.rules[field]](value);
        return true;
    }

    validateWithErrorBag(rule, field, value, errorBag) {
        if (!this.rules[rule]) throw new Error('Rule not found.');
        if (!errorBag || !(errorBag instanceof ErrorBag)) throw new Error('ErrorBag not included.');

        const isValid = this[this.rules[rule]](value);
        const inErrorBag = errorBag.items.findIndex(e => e.field === field) !== -1;
        if (!isValid && !inErrorBag) errorBag.add({ field, msg: '' });
        if (isValid && inErrorBag) errorBag.remove(field);
    }

    /*
    ** Rules
    */
    email(value) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    }
    required(value) {
        return /^\S.*$/.test(value);
    }
    dateOfBirth(value) {
        // Matches the format DD/MM/YYYY
        return /^(0[1-9]|[1-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/(19|20)\d{2}$/.test(value);
    }
}

export default {
    // pass reactive as an option to the plugin
    install(vueInstance, options) {

        if (vueInstance.version && vueInstance.version.startsWith('2.')) {
            // Vue 2 integration
            vueInstance.prototype.$validator = {
                errors: vueInstance.observable(new ErrorBag())
            };

            vueInstance.prototype.$setErrorBag = function(errorBag) {
                if (errorBag && errorBag instanceof ErrorBag) {
                    this.$validator.errors.items = [...errorBag.items];
                }
            };

            Object.defineProperty(vueInstance.prototype, 'errors', {
                get() {
                    return this.$validator.errors;
                }
            });
        } else if (vueInstance.config && vueInstance.config.globalProperties) {
            // Vue 3 integration

            const errorBag = new ErrorBag(); // Instantiate ErrorBag

            // Make errorBag reactive
            const reactiveErrorBag = options.reactive(errorBag);

            vueInstance.config.globalProperties.$validator = {
                errors: reactiveErrorBag
            };

            // Provide direct access to the reactive errorBag through a global property
            vueInstance.config.globalProperties.$setErrorBag = function(newErrorBag) {
                if (newErrorBag && newErrorBag instanceof ErrorBag) {
                    for (const key in newErrorBag) {
                        if (Object.prototype.hasOwnProperty.call(newErrorBag, key)) {
                            this.$validator.errors[key] = newErrorBag[key];
                        }
                    }
                }
            };

            // Direct access to errors via this.errors in components
            Object.defineProperty(vueInstance.config.globalProperties, 'errors', {
                get() {
                    return reactiveErrorBag;
                },
                set(newVal) {
                    if (newVal instanceof ErrorBag) {
                        for (const key in newVal) {
                            if (Object.prototype.hasOwnProperty.call(newVal, key)) {
                                reactiveErrorBag[key] = newVal[key];
                            }
                        }
                    }
                }
            });
        }
    }
};

/*
** This code works in Vue 2, but it uses mixins which are deprecated in Vue 3, but not removed yet.
** Also this gives an errors computed property to the component, which makes it easier to see the errors in the component.

function Validate(vueInstance) {
    vueInstance.mixin({
        beforeCreate() {
            // Initialize a new ErrorBag instance for each component
            this.$validator = {
                errors: vueInstance.observable(new ErrorBag())
            };
        },
        computed: {
            errors() {
                // Now it refers to the component's own ErrorBag instance
                return this.$validator.errors;
            }
        },
    });

    vueInstance.prototype.$setErrorBag = function(errorBag) {
        if (errorBag) {
            // Update the component's own ErrorBag items
            this.$validator.errors.items = [...errorBag.items];
        }
    };
}

* */
